import styles from './style.postcss';

import React from 'react';
import { withRouter } from 'react-router';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import is from 'is_js';
import { Map, List } from 'immutable';
import {
  connect,
  moment,
  ApiCall,
  PageCard,
  ConfirmDialog,
  Icon,
  PrivilegeChecker,
  Grid,
  Tooltip,
  testClass,
  ColourLabel
} from 'omni-common-ui';
import Privileges from 'domain/Privileges';
import GroupHomeworkCard from './GroupHomeworkCard';
import UnsavedChangesTrackingPageComponent from 'components/UnsavedChangesTrackingPageComponent';
import UnsavedChangesWarning from 'components/UnsavedChangesWarning';
import DeleteWarning from 'components/DeleteWarning';
import NoContentMessage from 'components/NoContentMessage';
import { actions as Group } from 'containers/Group';
import { actions as groupMembersActions } from 'data/GroupMembers';
import { actions as onlineHomeworkContentActions } from 'data/OnlineHomework/Content';
import { actions as personalisedHomeworkActions,
  accessors as personalisedHomeworkAccessors,
  PersonalisedHomework,
} from 'data/OnlineHomework/Personalised';
import { actions as UiState } from 'data/UiState';
import { actions as PaperbaseHomworkAction } from 'data/PaperBasedHomework';
import {
  actions as homeworkActions,
  status as HomeworkStatus,
  accessors as HomeworkAccessor
} from 'data/Homework';
import { accessors as User } from 'data/Users';
import ContentItem from './ContentItem';
import PersonalisedHomeworkFormCard from './PersonalisedHomeworkFormCard';
import PersonalisedHomeworkViewCard from './PersonalisedHomeworkViewCard';
import { actions as homeworkTemplateActions } from 'data/Homework/Template';
import { actions as HomeworkContentActions } from 'data/Homework/Content';
import { getUnitNumberById, getUnitList } from 'data/Units/accessors';
import { Modes } from './routes';
import PropTypes from 'prop-types';

const PUBLISHED = HomeworkStatus.PUBLISHED.toLowerCase();
const NEW = HomeworkStatus.NEW.toLowerCase();

export class Assignments extends UnsavedChangesTrackingPageComponent {
  constructor() {
    super();
    this.state = {
      formModel: undefined,
      isFormSaveable: false,
      formSaveFunction: () => {},
    };
    this._onSubmitPersonalisedHomework = this._onSubmitPersonalisedHomework.bind(this);
    this._onDiscardPersonalisedHomework = this._onDiscardPersonalisedHomework.bind(this);
    this._onFormNowSaveable = this._onFormNowSaveable.bind(this);
    this._onFormNotSaveable = this._onFormNotSaveable.bind(this);
  }

  componentWillMount() {
    this._fetchData(this.props);
    this._prepareEditor(this.props);
    super.componentWillMount();
  }

  componentWillReceiveProps(nextProps) {
    this._fetchData(nextProps);
    this._prepareEditor(this.props);

    // when moving between modes the form will no longer be saveable (fo sho')
    if (nextProps.params.mode !== this.props.params.mode) {
      this.setState({ isFormSaveable: false, formSaveFunction: () => {} });
    }
  }

  _fetchData(props) {
    const { currentSessionId } = props;
    this.props.fetchOnlineHomeworkContentIfNeeded(currentSessionId);
    if (this.props.hasGroupHomework) {
      this.props.fetchHomeworkContentIfNeeded(currentSessionId);
      this.props.fetchHomeworkTemplateIfNeeded(currentSessionId);
    }
    this.props.fetchHomeworkIfNeeded(currentSessionId);
  }

  _prepareEditor(props = this.props) {
    const { personalisedHomework, personalisedHomeworkViewModels, params } = props;
    if (Assignments.isInAddPersonalisedHomeworkMode(props)) {
      this.setState({ formModel: PersonalisedHomework.create() });
    } else if (Assignments.isInEditPersonalisedHomeworkMode(props) &&
        personalisedHomework) {
      const formModel = personalisedHomework.find((hw) =>
        hw.id === params.personalisedHomeworkId);
      const viewModel = personalisedHomeworkViewModels.find((hw) =>
        hw.id === params.personalisedHomeworkId);
      // FORM PRIVILEGE CHECK: Block edit if we don't have permission!
      if (! formModel || ! viewModel.canEdit) return;
      this.setState({ formModel });
    } else if (this.state.formModel) {
      this.setState({ formModel: undefined });
    }
  }

  _isChangesPresent() {
    // TODO: add checks for update here as it's implemented
    return !! this.state.isFormSaveable &&
        (Assignments.isInAddPersonalisedHomeworkMode(this.props) ||
        Assignments.isInEditPersonalisedHomeworkMode(this.props) ||
        Assignments.isInEditGroupHomeworkMode(this.props));
  }

  _onSubmitPersonalisedHomework(model, shouldStayOnPage = false) {
    const {
      currentSessionId,
      submitPersonalisedHomeworkModel,
      userId,
      buildRoute,
    } = this.props;
    // fill in missing bits to pretend we got this from the server :)
    const finalModel = model.merge({
      sessionId: currentSessionId,
      createdByUserId: userId,
    });
    submitPersonalisedHomeworkModel(finalModel)
    .then(() => {
      if (shouldStayOnPage) return;
      this._forcePushRoute(buildRoute({ mode: '', personalisedHomeworkId: '' }));
    });
  }

  _onDiscardPersonalisedHomework() {
    const { buildRoute } = this.props;
    this._forcePushRoute(buildRoute({ mode: '', personalisedHomeworkId: '' }));
  }

  _onFormNowSaveable() {
    const that = this;  // this is needed I don't know why
    this.setState({ isFormSaveable: true,
      formSaveFunction: () => {
        that.personalisedHomeworkComponent.getWrappedInstance().forceSubmit(true);
      },
    });
  }

  _onFormNotSaveable() {
    this.setState({ isFormSaveable: false, formSaveFunction: () => {} });
  }

  _getEditPersonalisedHomeworkLinkHref(personalisedHomeworkId) {
    const { buildRoute } = this.props;
    return buildRoute({
      mode: Modes.EDIT_PERSONALISED_HW,
      personalisedHomeworkId,
    });
  }

  _getDeletePersonalisedHomeworkLinkHref(personalisedHomeworkId) {
    const { buildRoute } = this.props;
    return buildRoute({
      mode: Modes.DELETE_PERSONALISED_HW,
      personalisedHomeworkId,
    });
  }

  _renderPersonalisedHomeworkForm(formModel = this.state.formModel) {
    const { students, personalisedHomework, group } = this.props;
    const { courseTypeLevelId } = group;
    const personalisedHomeworkIndex =
        personalisedHomework && personalisedHomework.indexOf(formModel);
    const formHeadingText =
        is.number(personalisedHomeworkIndex) && personalisedHomeworkIndex > - 1 ?
        `Personalised Practice ${personalisedHomeworkIndex + 1}` :
        'Personalised Practice';

    if (is.undefined(students)) {
      return null;
    }

    return <PersonalisedHomeworkFormCard ref={(_cmpnt) => {
      this.personalisedHomeworkComponent = _cmpnt;
    }}
        courseTypeLevelId={courseTypeLevelId}
        model={formModel}
        overrideHeadingText={formHeadingText}
        students={students}
        onSubmit={this._onSubmitPersonalisedHomework}
        onDiscard={this._onDiscardPersonalisedHomework}
        onNowSaveable={this._onFormNowSaveable}
        onNotSaveable={this._onFormNotSaveable} />;
  }

  _renderOnlineSection(onlineHomeworkContent) {
    return <PageCard className={testClass('assignHomework-online-content')}>
      <PageCard.Heading text="Online" stackMode="horizontal">
        <Tooltip text="Locked"
            placement="top"
            trigger={['hover']}>
          <Icon id="lock" />
        </Tooltip>
      </PageCard.Heading>
      <PageCard.Content className={styles.AssignHomework_online_content}>
        {onlineHomeworkContent.map((content) =>  // eslint-disable-line no-confusing-arrow
          is.not.empty(content.contentItems) ? [
            <h4 key={content.lessonNumber}>
              Unit {content.unitOrderingSequence}, Lesson {content.lessonNumber}
            </h4>,
            content.contentItems.map((item) =>
              <ContentItem key={`${content.lessonNumber}-${item.name}`}
                  boardTitle={item.name}
                  boardValue={item.description} />),
          ] : null)}
      </PageCard.Content>
    </PageCard>;
  }

  _renderOnlineHomeworkContent() {
    const { onlineHomeworkContent } = this.props;
    if (! onlineHomeworkContent || onlineHomeworkContent.size === 0) return;
    return this._renderOnlineSection(onlineHomeworkContent);
  }

  _renderPersonalisedHomework() {
    const {
      group,
      personalisedHomework,
      personalisedHomeworkViewModels,
      students,
    } = this.props;
    const { courseTypeLevelId } = group;
    if (is.undefined(personalisedHomework) || personalisedHomework.size === 0) {
      return null;
    }

    return personalisedHomeworkViewModels.map((ph, index) => {
      const phStudents = students &&
          students.filter((s) => ph.studentIds.includes(s.studentGeneralInfo.id));
      if (is.falsy(phStudents)) {
        return null;
      }
      if (ph && this.state.formModel && ph.id === this.state.formModel.id) {
        return null;
      }
      return <PersonalisedHomeworkViewCard key={ph.id}
          students={phStudents}
          index={index}
          contentClassName={styles.AssignHomework_online_content}
          editPersonalisedHomeworkLinkHref={this._getEditPersonalisedHomeworkLinkHref(ph.id)}
          deletePersonalisedHomeworkLinkHref={this._getDeletePersonalisedHomeworkLinkHref(ph.id)}
          personalisedHomework={ph}
          courseTypeLevelId={courseTypeLevelId} />;
    });
  }

  _renderUnsavedChangesWarningDialog() {
    return <UnsavedChangesWarning isOpen={
          !! this._isNavigationWithChangesRequested()}
        isSaving={this.props.isAnyApiCallSaving}
        onSaveClicked={this.state.formSaveFunction}
        onDiscardClicked={() => {
          this._forcePushRequestedLocation();
        }}
        onCloseClicked={() => {
          this._clearRequestedLocation();
        }}
        onSaved={() => {
          this._forcePushRequestedLocation();
        }} />;
  }

  _renderDeleteWarningDialog() {
    const {
      buildRoute,
      pushRoute,
      params: { personalisedHomeworkId },
      personalisedHomework,
      personalisedHomeworkViewModels,
    } = this.props;
    const viewModel = personalisedHomeworkViewModels &&
        personalisedHomeworkViewModels.find((hw) =>
            hw.id === personalisedHomeworkId);
    // DELETE PRIVILEGE CHECK: Block delete if we don't have permission!
    if (! viewModel || ! viewModel.canEdit) return;
    const clearDeleteWarning = () => {
      pushRoute(buildRoute({ mode: '', personalisedHomeworkId: null }));
    };
    const onDeleteClicked = () => {
      personalisedHomeworkId &&
          this.props.deletePersonalisedHomework(personalisedHomeworkId);
      clearDeleteWarning();
    };
    const modelIndex = personalisedHomework.findIndex((hw) => hw.id === personalisedHomeworkId);
    return <DeleteWarning isOpen={
          Assignments.isInDeletePersonalisedHomeworkMode(this.props)}
        warningMessage={`Are you sure you want to delete Personalised Homework ${modelIndex + 1}?`}
        isDeleting={this.props.isAnyApiCallSaving}
        onDeleteClicked={onDeleteClicked}
        onDiscardClicked={clearDeleteWarning}
        onCloseClicked={clearDeleteWarning} />;
  }

  _renderPublishDialog() {
    const { buildRoute, pushRoute, isHasPublishPrivilege } = this.props;
    // PUBLISH PRIVILEGE CHECK: Don't show this dialog if we don't have permission!
    if (! isHasPublishPrivilege) return null;
    const returnToView = () => {
      pushRoute(buildRoute({ mode: '' }));
    };
    return <ConfirmDialog isOpen={
          Assignments.isInPublishMode(this.props)}
        isLoading={this.props.isAnyApiCallSaving}
        primaryButtonContent="Publish"
        secondaryButtonContent="Cancel"
        onPrimaryClick={() => this._publishHomework()}
        onSecondaryClick={returnToView}
        onRequestClose={returnToView}>
      <span>
        Content submitted cannot be edited.
      </span>
    </ConfirmDialog>;
  }

  _renderClearDialog() {
    const { buildRoute, pushRoute } = this.props;
    const returnToView = () => {
      pushRoute(buildRoute({ mode: '' }));
    };
    return <ConfirmDialog isOpen={
          Assignments.isInClearMode(this.props)}
        isLoading={this.props.isAnyApiCallSaving}
        primaryButtonContent="Continue"
        secondaryButtonContent="Cancel"
        onPrimaryClick={() => this._clearHomework()}
        onSecondaryClick={returnToView}
        onRequestClose={returnToView}>
      <span>
        Please note that clicking 'Continue' will clear your assigned practice
         and clear the practice completion for this practice.
      </span>
    </ConfirmDialog>;
  }

  _publishHomework() {
    const {
      buildRoute,
      pushRoute,
      publishHomework,
      homework,
      publishPersonalisedHomework,
      personalisedHomework,
      currentSessionId,
      fetchHomeworkContent,
    } = this.props;
    Promise.all(
      [publishHomework(homework.sessionId)]
      .concat(personalisedHomework
        .filter((ph) => ph.status && ph.status.toLowerCase() !== PUBLISHED)
        .map((ph) => publishPersonalisedHomework(ph.id))))
      .then(() => {
        pushRoute(buildRoute({ mode: '' }));
      }).then(() => fetchHomeworkContent(currentSessionId));
  }

  _clearHomework() {
    const {
      buildRoute,
      pushRoute,
      clearHomework,
      currentSessionId,
      fetchHomeworkContent,
      fetchPaperBasedHomework,
    } = this.props;

    return clearHomework(currentSessionId)
      .then(() => {
        pushRoute(buildRoute({ mode: '' }));
      }).then(() => {
        fetchHomeworkContent(currentSessionId);
        fetchPaperBasedHomework(currentSessionId);
      });
  }

  _getAssignedGroupHomework(editedGroupHomeworks) {
    const toSaveHomeworkItems = editedGroupHomeworks.map((homwork) =>
    homwork.update('contentItems', (contentItems) =>
      contentItems.filter((item) => !! item.isAssigned)));
    return toSaveHomeworkItems;
  }

  _renderGroupHomeworkContent() {
    const {
      homework,
      homeworkContent,
      assignHomeworkContent,
      currentSessionId,
      markable,
      currentSessionDate,
      homeworkTemplate,
      buildRoute,
    } = this.props;
    if (! homework ||
        ((! homeworkTemplate || homeworkTemplate.size === 0) &&
        (! homeworkContent || homeworkContent.size === 0))) {
      return;
    }

    return <GroupHomeworkCard homeworkContent={homeworkContent}
        homeworkTemplate={homeworkTemplate}
        isNewStatus={homework.homeworkResultStatus.toLowerCase() === NEW}
        editMode={Assignments.isInEditGroupHomeworkMode(this.props) &&
                  this.props.canEditHomework}
        onEditClick={() => {
          this._forcePushRoute(buildRoute({ mode: Modes.EDIT_GROUP_HOMEWORK, personalisedHomeworkId: '' }));
        }}
        onNowSaveable={(groupHomeworkItems) =>
          this.setState({ isFormSaveable: true,
            formSaveFunction: () => {
              assignHomeworkContent(
                currentSessionId,
                this._getAssignedGroupHomework(groupHomeworkItems));
            } })}
        onSaveClick={(groupHomeworkItems) => {
          this.setState({ isFormSaveable: false, formSaveFunction: () => {} });
          assignHomeworkContent(currentSessionId, groupHomeworkItems);
          this._forcePushRoute(buildRoute({ mode: '' }));
        }}
        onDiscardClick={() => {
          this.setState({ isFormSaveable: false, formSaveFunction: () => {} });
          this._forcePushRoute(buildRoute({ mode: '' }));
        }}
        canEdit={markable && moment().isAfter(currentSessionDate) &&
          ! Assignments.isInAddPersonalisedHomeworkMode(this.props) &&
          ! Assignments.isInEditPersonalisedHomeworkMode(this.props) &&
          ! Assignments.isInPublishMode(this.props) &&
          ! Assignments.isInClearMode(this.props) &&
          ! Assignments.isInEditGroupHomeworkMode(this.props) &&
          this.props.canEditHomework} />;
  }

  _renderContent() {
    const {
      timeZone,
      sessions,
      currentSessionId,
      onlineHomeworkContent,
      homeworkContent,
      homeworkTemplate,
      isAnyApiCallLoading, // eslint-disable-line
      sessionHasPersonalisedHomework,
      isCurrentHomeowrkPublished,
    } = this.props;

    const hasContent = (onlineHomeworkContent && onlineHomeworkContent.size > 0) ||
      (homeworkTemplate && homeworkTemplate.size > 0) ||
      (homeworkContent && homeworkContent.size > 0) ||
      sessionHasPersonalisedHomework ||
      this.state.formModel;

    if (! hasContent) {
      if (isAnyApiCallLoading) return;  // loading?
      return <Grid outerMargin>
        <Grid.Group>
          <NoContentMessage text="No practice was assigned." />
        </Grid.Group>
      </Grid>;
    }

    const currentSession = sessions.get(currentSessionId);
    const currentSessionIndex = currentSession.index - 1;
    const sessionList = sessions.toList().sortBy((session) => session.index);
    const nextSession = sessionList.get(currentSessionIndex + 1);

    return <Grid outerMargin>
      <Grid.Group>
        {
          nextSession &&
          <div className={styles.AssignHomework_header}>
            <span className={styles.AssignHomework_header_duedate}>
              {`Due On ${nextSession.period.start.clone().tz(timeZone).format('dddd DD of MMMM')}`}
            </span>
            {isCurrentHomeowrkPublished && <ColourLabel text="published" colour="green" />}
          </div>
        }
        {
          this.state.formModel &&
          this._renderPersonalisedHomeworkForm(this.state.formModel)
        }
        {this._renderOnlineHomeworkContent()}
        {this.props.hasGroupHomework && this._renderGroupHomeworkContent()}
        {this._renderPersonalisedHomework()}
      </Grid.Group>
    </Grid>;
  }

  render() {
    return <div>
      {this._renderContent()}
      {this._renderUnsavedChangesWarningDialog()}
      {this._renderDeleteWarningDialog()}
      {this._renderPublishDialog()}
      {this._renderClearDialog()}
    </div>;
  }
}

Assignments.isInAddPersonalisedHomeworkMode = (props) =>
  props.params.mode === Modes.CREATE_PERSONALISED_HW;

Assignments.isInEditPersonalisedHomeworkMode = (props) =>
  props.params.mode === Modes.EDIT_PERSONALISED_HW;

Assignments.isInDeletePersonalisedHomeworkMode = (props) =>
  props.params.mode === Modes.DELETE_PERSONALISED_HW;

Assignments.isInPublishMode = (props) =>
  props.params.mode === Modes.PUBLISH;

Assignments.isInClearMode = (props) =>
  props.params.mode === Modes.CLEAR;

Assignments.isInEditGroupHomeworkMode = (props) =>
  props.params.mode === Modes.EDIT_GROUP_HOMEWORK;

Assignments.propTypes = {
  params: PropTypes.shape({
    groupId: PropTypes.string,
    sessionPlanId: PropTypes.string,
  }),
  isCurrentHomeowrkPublished: PropTypes.bool,
  router: PropTypes.any.isRequired,
  routes: PropTypes.array.isRequired,
  route: PropTypes.any.isRequired,
  location: PropTypes.object.isRequired,
  pushRoute: PropTypes.func.isRequired,
  buildRoute: PropTypes.func.isRequired,
  currentSessionId: PropTypes.string,
  currentSessionDate: PropTypes.object,
  showNavigationBar: PropTypes.bool,
  sessions: PropTypes.instanceOf(Map),
  timeZone: PropTypes.object,
  group: PropTypes.object,
  hasGroupHomework: PropTypes.bool.isRequired,
  students: PropTypes.object,
  units: PropTypes.instanceOf(Map),
  onlineHomeworkContent: PropTypes.shape({
    map: PropTypes.func,
    size: PropTypes.number,
  }),
  homeworkTemplate: PropTypes.shape({
    map: PropTypes.func,
    size: PropTypes.number,
  }),
  homeworkContent: PropTypes.shape({
    map: PropTypes.func,
    size: PropTypes.number,
  }),
  homework: PropTypes.object,
  sessionHasPersonalisedHomework: PropTypes.bool,
  personalisedHomework: PropTypes.object,
  personalisedHomeworkViewModels: PropTypes.object,
  fetchOnlineHomeworkContentIfNeeded: PropTypes.func,
  fetchHomeworkContentIfNeeded: PropTypes.func,
  fetchHomeworkContent: PropTypes.func,
  assignHomeworkContent: PropTypes.func,
  fetchHomeworkIfNeeded: PropTypes.func,
  fetchSessionAttendanceIfNeeded: PropTypes.func,
  fetchHomeworkTemplateIfNeeded: PropTypes.func,
  publishHomework: PropTypes.func,
  clearHomework: PropTypes.func,
  publishPersonalisedHomework: PropTypes.func,
  submitPersonalisedHomeworkModel: PropTypes.func,
  deletePersonalisedHomework: PropTypes.func,
  fetchPaperBasedHomework: PropTypes.func,
  markable: PropTypes.bool,
  isAnyApiCallSaving: PropTypes.bool,
  isHasPublishPrivilege: PropTypes.bool,
  userId: PropTypes.string,
  canEditHomework: PropTypes.bool,
};

export function mapStateToProps(state, ownProps) {
  const { groupId, sessionPlanId } = ownProps.params;
  const group = state.get('rootReducer').get('groups').get(groupId).data;
  const hasGroupHomework = Group.hasPaperBasedHomework(state, groupId);
  const groupMembers = groupMembersActions.getMembersByGroup(state, group.id);
  const sessions = state.get('rootReducer').get('sessionPlans').filter(
    (session) => session.groupId === group.id);
  const currentSessionMembers = sessions.get(sessionPlanId).sessionMembers;
  const currentSessionDate = sessions.get(sessionPlanId).period.start;
  const units = getUnitList(state, groupId);
  const { timeZone } = Group.getCentre(state, groupId);
  const showNavigationBar = UiState.getShowNavigationBar(state);
  const isCurrentHomeowrkPublished = HomeworkAccessor.isExisty(state, sessionPlanId) &&
    HomeworkAccessor.isPublished(state, sessionPlanId);

  let homeworkTemplate = state.get('rootReducer').get('homeworkTemplate').get(sessionPlanId);
  homeworkTemplate = homeworkTemplate && homeworkTemplate.map((content) =>
      content.set('unitOrderingSequence', getUnitNumberById(units, content.unitId)))
        .sort(sortHomeworkContent);
  let onlineHomeworkContent = state.get('rootReducer').get('onlineHomeworkContent').get(sessionPlanId);
  onlineHomeworkContent = onlineHomeworkContent && onlineHomeworkContent.map((content) =>
      content.set('unitOrderingSequence', getUnitNumberById(units, content.unitId)))
        .sort(sortHomeworkContent);
  let homeworkContent = state.get('rootReducer').get('homeworkContent').get(sessionPlanId);
  homeworkContent = homeworkContent && homeworkContent.map((content) =>
      content.set('unitOrderingSequence', getUnitNumberById(units, content.unitId)))
        .sort(sortHomeworkContent);
  const students = is.undefined(currentSessionMembers) ?
    undefined :
    groupMembers.filter((member) => is.not.undefined(
      currentSessionMembers.find(
        (currentSessionMember) =>
           currentSessionMember === member.studentGeneralInfo.id
      ))
    );

  const homework = state.get('rootReducer').get('homework').get(sessionPlanId);
  const personalisedHomeworkState = state.get('rootReducer').get('personalisedHomework');
  let personalisedHomework;
  let personalisedHomeworkViewModels;
  if (is.not.undefined(homework)) {
    personalisedHomework = personalisedHomeworkState.filter(
      (ph) => `${ph.sessionId}` === `${sessionPlanId}`)
    ;
    if (personalisedHomework) {
      personalisedHomework = personalisedHomework &&
          personalisedHomework.toList().sortBy((ph) => ph.id);
      personalisedHomeworkViewModels = personalisedHomework.map((ph) =>
        personalisedHomeworkAccessors.getPersonalisedHomeworkViewModel(state, groupId, ph.id,
          Assignments.isInEditGroupHomeworkMode({ params: ownProps.params })));
    }
  }
  const markable = is.existy(currentSessionMembers) && homework &&
    checkPersonalisedHomeworkStatus(personalisedHomework, homework);
  const sessionHasPersonalisedHomework = personalisedHomework && !! personalisedHomework.size;
  return {
    currentSessionId: sessionPlanId,
    isCurrentHomeowrkPublished,
    hasGroupHomework,
    groupId,
    group,
    students,
    sessions,
    units,
    timeZone,
    showNavigationBar,
    onlineHomeworkContent,
    homeworkContent,
    homeworkTemplate,
    currentSessionDate,
    homework,
    markable,
    sessionHasPersonalisedHomework,
    personalisedHomework,
    personalisedHomeworkViewModels,
    userId: User.getLoginUserId(state),
    isAnyApiCallLoading: !! isAnyApiCallLoading(state),
    isAnyApiCallSaving: !! isAnyApiCallSaving(state),
    isHasPublishPrivilege: PrivilegeChecker.hasPrivilege(state, Privileges.AssignHomework.Publish),
    canEditHomework: ! Group.isAutoPublishHomework(state, groupId) &&
      PrivilegeChecker.hasPrivilege(state, Privileges.AssignHomework.EditAny),
  };
}

function sortHomeworkContent(a, b) {
  if (a.unitOrderingSequence !== b.unitOrderingSequence) {
    return a.unitOrderingSequence - b.unitOrderingSequence;
  }
  return a.lessonNumber - b.lessonNumber;
}

function checkPersonalisedHomeworkStatus(phs, hw) {
  const publishedPhs = phs.filter((ph) =>
    ph.status.toLowerCase() === PUBLISHED
  );
  return (phs.size === 0 || phs.size !== publishedPhs.size) &&
    (hw.homeworkResultStatus.toLowerCase() !== PUBLISHED);
}

function isAnyApiCallLoading(state) {
  return !! (state.get('apiCalls') || new List())
    .filter((call, key) =>
      ['GET'].some((method) => key.startsWith(method)))
    .find((call) => ApiCall.State.isLoading(call));
}

function isAnyApiCallSaving(state) {
  return !! (state.get('apiCalls') || new List())
    .filter((call, key) =>
      ['PUT', 'POST', 'DELETE'].some((method) => key.startsWith(method)))
    .find((call) => ApiCall.State.isLoading(call));
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(onlineHomeworkContentActions, dispatch),
    bindActionCreators(personalisedHomeworkActions, dispatch),
    bindActionCreators(homeworkActions, dispatch),
    bindActionCreators(HomeworkContentActions, dispatch),
    bindActionCreators(homeworkTemplateActions, dispatch),
    bindActionCreators(PaperbaseHomworkAction, dispatch),
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Assignments));



// WEBPACK FOOTER //
// ./containers/Group/AssignHomework/Assignments/component.jsx